import React, { useCallback, useEffect, useMemo, useState } from "react";
import { classnames } from "@culturehq/components";

import { useDocumentEvent, useFullScreen } from "app/hooks";

import RocketSVG from "components/common/RocketSVG";

import styles from "./slides.module.scss";

const makeTargetStyles = () => {
  const maximum = Math.max(window.innerWidth, window.innerHeight);

  const targetStyles = { visibility: "visible", transition: "transform 30s linear" };
  const left = Math.floor(Math.random() * (window.innerWidth / 3));

  switch (Math.floor(Math.random() * 4)) {
    case 0:
      return {
        svg: {
          ...targetStyles,
          left: `${left}px`,
          top: `${window.innerHeight}px`,
          transform: `translate3d(${maximum}px, -${maximum}px, 0)`
        },
        rotation: 0
      };
    case 1:
      return {
        svg: {
          ...targetStyles,
          left: `${window.innerWidth - left}px`,
          top: `${window.innerHeight}px`,
          transform: `translate3d(-${maximum}px, -${maximum}px, 0)`
        },
        rotation: -90
      };
    case 2:
      return {
        svg: {
          ...targetStyles,
          left: `${window.innerWidth - left}px`,
          top: "-5vh",
          transform: `translate3d(-${maximum}px, ${maximum}px, 0)`
        },
        rotation: 180
      };
    case 3:
      return {
        svg: {
          ...targetStyles,
          left: `${left}px`,
          top: "-5vh",
          transform: `translate3d(${maximum}px, ${maximum}px, 0)`
        },
        rotation: 90
      };
    default:
      return {};
  }
};

const Rocket = () => {
  const [targetStyles, setTargetStyles] = useState({});

  const onLaunch = useCallback(
    () => {
      setTargetStyles({});
      setTimeout(
        () => setTargetStyles(makeTargetStyles()),
        Math.floor(Math.random() * 15) + 5
      );
    },
    [setTargetStyles]
  );

  useEffect(
    () => {
      const timeout = setTimeout(onLaunch, (Math.round(Math.random() * 25) + 20) * 1000);
      return () => clearTimeout(timeout);
    },
    [onLaunch]
  );

  return (
    <>
      <svg
        viewBox="0 0 500 500"
        className={classnames("rocket", styles.rocket)}
        onTransitionEnd={onLaunch}
        style={targetStyles.svg}
      >
        <g transform={`rotate(${targetStyles.rotation || 0}, 250, 250)`}>
          <RocketSVG />
        </g>
      </svg>
    </>
  );
};

const makeColor = () => {
  const random = Math.random();

  if (random < 0.1) {
    return styles["star-blue"];
  }
  if (random < 0.2) {
    return styles["star-green"];
  }
  if (random < 0.3) {
    return styles["star-yellow"];
  }
  return null;
};

const makePercent = () => Math.round(Math.random() * 10000) / 100;

const makeStars = num => Array(num).fill().map(() => {
  const size = `${Math.floor((Math.random() * 6) + 3)}px`;

  return {
    color: makeColor(),
    style: {
      animation: Math.random() < 0.2 ? `${styles.flicker} 20s linear infinite` : "none",
      animationDelay: `${Math.floor(Math.random() * 20)}s`,
      left: `${makePercent()}%`,
      top: `${makePercent()}%`,
      width: size,
      height: size
    }
  };
});

const makeShooting = num => Array(num).fill().map(() => ({
  color: makeColor(),
  style: {
    left: `${makePercent() * 0.8}%`,
    top: `${makePercent() * 0.8}%`
  },
  animation: {
    animationDelay: `${Math.round(Math.random() * 30) + 10}s`
  }
}));

export const SlidesBackground = ({ children, className, containerRef, starCount = 500 }) => {
  const stars = useMemo(() => makeStars(starCount), [starCount]);
  const shooting = useMemo(() => makeShooting(5), []);

  return (
    <div ref={containerRef} className={classnames(styles.slides, className)}>
      {stars.map((star, index) => (
        <div
          key={index}
          className={classnames(styles.star, star.color)}
          style={star.style}
        />
      ))}
      {shooting.map((star, index) => (
        <div
          key={index}
          className={classnames(styles.star, styles["star-shooting"])}
          style={star.style}
        >
          <div className={star.color} style={star.animation} />
        </div>
      ))}
      <Rocket />
      {children}
    </div>
  );
};

const Slide = ({ children, className }) => (
  <div className={classnames(styles.slide, className)}>
    {children}
  </div>
);

const useSlides = onFetch => {
  const [slides, setSlides] = useState([]);

  useEffect(
    () => {
      let cancelled = false;

      onFetch()
        .then(response => {
          if (!cancelled) {
            setSlides(response);
          }
        });

      return () => {
        cancelled = true;
      };
    },
    [onFetch, setSlides]
  );

  return slides;
};

const useSlideIndex = slides => {
  const [paused, setPaused] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);

  useDocumentEvent(
    "keydown",
    useCallback(
      event => {
        if (event.key === " ") {
          setPaused(value => !value);
        }
      },
      [setPaused]
    )
  );

  useEffect(
    () => {
      if (!paused && slides.length > 0) {
        const interval = setInterval(
          () => setSlideIndex(value => (value + 1) % slides.length),
          10000
        );

        return () => clearInterval(interval);
      }

      return undefined;
    },
    [paused, slides, setSlideIndex]
  );

  return slideIndex;
};

const Slides = ({ onClose, onFetch, onRender }) => {
  const slides = useSlides(onFetch);
  const slideIndex = useSlideIndex(slides);

  const left = slides[(slideIndex - 1 + slides.length) % slides.length] || {};
  const center = slides[slideIndex] || {};
  const right = slides[(slideIndex + 1) % slides.length] || {};

  const [containerRef, onRequest] = useFullScreen(onClose);
  useEffect(() => void onRequest(), [onRequest]);

  return (
    <SlidesBackground containerRef={containerRef} className={styles["slides-full"]}>
      {slides.length > 0 && (
        <>
          <Slide key={left.id} className={styles["slide-left"]}>
            {onRender(left)}
          </Slide>
          <Slide key={center.id} className={styles["slide-center"]}>
            {onRender(center)}
          </Slide>
          <Slide key={right.id} className={styles["slide-right"]}>
            {onRender(right)}
          </Slide>
        </>
      )}
    </SlidesBackground>
  );
};

export default Slides;
